// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Blog imports
import Blog from './pages/Blog';
import AdminBlog from './pages/AdminBlog';
import BlogEditor from './pages/BlogEditor';
import UserBlog from './pages/UserBlog';

// Admin Section imports
import AdminUsersManage from './pages/AdminUsersManage';
import SectionManage from './pages/SectionManage';
import AboutUsManage from './pages/AboutUsManage';
import AchievementsManage from './pages/AchievementsManage';
import FrontPageManage from './pages/FrontPageManage';
import ContactsManage from './pages/ContactsManage';
import Analytics from './pages/Analytics';
import AdminDiets from './pages/AdminDiets';

// Other pages
import AboutPage from './pages/AboutPage';
import DietsPage from './pages/DietsPage';
import AchievementsPage from './pages/AchievementsPage';
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import Register from './pages/Register';
import ProgramsPage from './pages/ProgramsPage';
import AdminDashboard from './pages/AdminDashboard';
import ProgramDays from './pages/ProgramDays';
import DayVideos from './pages/DayVideos';
import DayList from './pages/DayList';
import MyProfile from './pages/MyProfile';
import UserBuyPrograms from './pages/UserBuyPrograms';
import TrainGym from './pages/TrainGym';
import UserDashboard from './pages/UserDashboard';
import ProgramList from './pages/ProgramList';
import BuyGymExercise from './pages/BuyGymExercise';
import UserBuyDiets from './pages/UserBuyDiets';
import ProgramDaysView from './pages/ProgramDaysView';
import VideoPlayerPage from './pages/VideoPlayerPage';
import VerifyEmail from './pages/VerifyEmail';

// ✅ ScrollToTop Component
const ScrollToTop = () => {
    const { pathname } = useLocation();
    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [pathname]);
    return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null }) => {
    const { user, isAuthenticated, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                 minHeight="100vh" bgcolor="background.default">
                <CircularProgress sx={{ color: 'primary.main', mb: 2 }} size={60} />
                <Typography variant="h6" color="text.secondary">
                    Проверка на овластувања...
                </Typography>
            </Box>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (requiredRole && user?.role !== requiredRole) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                 minHeight="100vh" bgcolor="background.default" textAlign="center" p={4}>
                <Typography variant="h1" sx={{ fontSize: '120px', mb: 2 }}>🚫</Typography>
                <Typography variant="h3" color="error" gutterBottom>
                    Пристапот е одбиен
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                    Немате дозвола за пристап до оваа страница.
                </Typography>
                <Button variant="outlined" onClick={() => window.location.href = '/'} sx={{ mt: 2 }}>
                    🏠 Назад кон почетната
                </Button>
            </Box>
        );
    }

    return children;
};

function App() {
    return (
        <ThemeProvider>
            <CssBaseline />
            <AuthProvider>
                <Router>
                    <ScrollToTop />
                    <div className="App">
                        <Routes>
                            {/* ===== PUBLIC ROUTES ===== */}
                            <Route path="/" element={<HomePage />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/programs" element={<ProgramsPage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/diets" element={<DietsPage />} />
                            <Route path="/achievements" element={<AchievementsPage />} />
                            <Route path="/blog" element={<Blog />} />

                            {/* ===== USER ROUTES ===== */}
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/user-dashboard" element={
                                <ProtectedRoute><UserDashboard /></ProtectedRoute>
                            } />
                            <Route path="/dashboard" element={
                                <ProtectedRoute><UserDashboard /></ProtectedRoute>
                            } />
                            <Route path="/buy-programs" element={
                                <ProtectedRoute><UserBuyPrograms /></ProtectedRoute>
                            } />
                            {/* ✅ POPRAVENO - bez razmak */}
                            <Route path="/program/:programId/days" element={
                                <ProtectedRoute><ProgramDaysView /></ProtectedRoute>
                            } />
                            <Route path="/gym-training" element={
                                <ProtectedRoute><BuyGymExercise /></ProtectedRoute>
                            } />
                            <Route path="/user-diets" element={
                                <ProtectedRoute><UserBuyDiets /></ProtectedRoute>
                            } />
                            <Route path="/profile" element={
                                <ProtectedRoute><MyProfile /></ProtectedRoute>
                            } />
                            <Route path="/my-programs" element={
                                <ProtectedRoute><UserBuyPrograms /></ProtectedRoute>
                            } />
                            <Route path="/user-blog" element={
                                <ProtectedRoute><UserBlog /></ProtectedRoute>
                            } />

                            {/* ===== ADMIN ROUTES ===== */}
                            <Route path="/admin" element={
                                <ProtectedRoute requiredRole="ADMIN"><AdminDashboard /></ProtectedRoute>
                            } />
                            <Route path="/admin/programs" element={
                                <ProtectedRoute requiredRole="ADMIN"><ProgramList /></ProtectedRoute>
                            } />
                            {/* ✅ POPRAVENO - bez razmak */}
                            <Route path="/admin/programs/:programId/days" element={
                                <ProtectedRoute requiredRole="ADMIN"><ProgramDays /></ProtectedRoute>
                            } />
                            <Route path="/admin/days" element={
                                <ProtectedRoute requiredRole="ADMIN"><DayList /></ProtectedRoute>
                            } />
                            <Route path="/admin/days/:dayId" element={
                                <ProtectedRoute requiredRole="ADMIN"><DayVideos /></ProtectedRoute>
                            } />
                            <Route path="/admin/train-gym" element={
                                <ProtectedRoute requiredRole="ADMIN"><TrainGym /></ProtectedRoute>
                            } />
                            <Route path="/admin/users" element={
                                <ProtectedRoute requiredRole="ADMIN"><AdminUsersManage /></ProtectedRoute>
                            } />
                            <Route path="/admin/diets" element={
                                <ProtectedRoute requiredRole="ADMIN"><AdminDiets /></ProtectedRoute>
                            } />

                            {/* ===== ADMIN SECTIONS ===== */}
                            <Route path="/admin/sections" element={
                                <ProtectedRoute requiredRole="ADMIN"><SectionManage /></ProtectedRoute>
                            } />
                            <Route path="/admin/sections/about-us" element={
                                <ProtectedRoute requiredRole="ADMIN"><AboutUsManage /></ProtectedRoute>
                            } />
                            <Route path="/admin/sections/achievements" element={
                                <ProtectedRoute requiredRole="ADMIN"><AchievementsManage /></ProtectedRoute>
                            } />
                            <Route path="/admin/sections/front-page" element={
                                <ProtectedRoute requiredRole="ADMIN"><FrontPageManage /></ProtectedRoute>
                            } />
                            <Route path="/admin/sections/contacts" element={
                                <ProtectedRoute requiredRole="ADMIN"><ContactsManage /></ProtectedRoute>
                            } />
                            <Route path="/admin/analytics" element={
                                <ProtectedRoute requiredRole="ADMIN"><Analytics /></ProtectedRoute>
                            } />

                            {/* ===== ADMIN BLOG ===== */}
                            <Route path="/admin/blog" element={
                                <ProtectedRoute requiredRole="ADMIN"><AdminBlog /></ProtectedRoute>
                            } />
                            <Route path="/admin/blog/create" element={
                                <ProtectedRoute requiredRole="ADMIN"><BlogEditor /></ProtectedRoute>
                            } />
                            <Route path="/admin/blog/edit/:id" element={
                                <ProtectedRoute requiredRole="ADMIN"><BlogEditor /></ProtectedRoute>
                            } />
                            <Route path="/verify-email" element={<VerifyEmail />} />

                            {/* ===== VIDEO PLAYER ===== */}
                            {/* ✅ POPRAVENO - bez razmak */}
                            <Route path="/video/:videoId" element={
                                <ProtectedRoute><VideoPlayerPage /></ProtectedRoute>
                            } />

                            {/* ===== CATCH ALL ===== */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </div>
                </Router>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;